import type { QuestionBankFilter, QuestionBankSource, QuestionBankType } from './types.ts';

/**
 * The server half of Kho bài tập: filter -> /question/admin + /question/count query, and the pager maths.
 *
 * Mirrors the admin's ListQuestions.vue (html_contain, type, level, createdBy, _limit/_skip/_sort).
 * The gateway hands the controller single-value query params, so `type_in` carries one value only:
 * a list of board types cannot be sent. With no type picked the query sends isFilterType (drops the
 * *-child rows) and the count includes the group / fill-blank rows the board hides; each page says how
 * many it hid. isFilterType also overwrites `type`, so it is never sent together with a type.
 */

export const PAGE_SIZES = [10, 20, 50];
export const DEFAULT_PAGE_SIZE = 20;

export type ServerQuery = Record<string, string | number | boolean>;

/** Board type -> the IZ row it is stored as. Đúng - Sai is a single-choice marked in config.msb. */
const TYPE_QUERY: Record<QuestionBankType, ServerQuery> = {
  'single-choice': { type: 'single-choice' },
  'multiple-choice': { type: 'multiple-choice' },
  text: { type: 'text' },
  boolean: { type: 'single-choice', 'config.msb.type': 'boolean' },
};

/** Board sources live in config.msb; rows made in the admin carry none. */
const SOURCE_QUERY: Record<QuestionBankSource, ServerQuery> = {
  'ai-quiz-supporter': { 'config.msb.source': 'ai-quiz-supporter' },
  manual: { 'config.msb.source': 'manual' },
};

/** Exactly the rows a board source stamped - the legacy dedupe reads both. */
export function boardSourceQuery(source: QuestionBankSource): ServerQuery {
  return SOURCE_QUERY[source];
}

/** Kho đề AI or Kho đề gốc; gốc is every row the AI did not make, admin rows included. */
export function bankTab(filter: QuestionBankFilter): QuestionBankSource {
  return filter.source === 'ai-quiz-supporter' ? 'ai-quiz-supporter' : 'manual';
}

/** html_contain is a Mongo $regex, so the teacher's text is escaped to match literally. */
export function escapeRegex(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** The filters /question/admin and /question/count both read. Null `userId` with `mine` is the caller's to refuse. */
export function serverFilter(filter: QuestionBankFilter, userId: string | null): ServerQuery {
  const query: ServerQuery = {};
  const search = (filter.search || '').trim();
  if (search) query.html_contain = escapeRegex(search);
  if (filter.mine && userId) query.createdBy = userId;
  // Khó is IZ level 3 only: VDC (4) would need a second value the gateway cannot carry
  if (filter.level && filter.level !== 'all') query.level = filter.level;
  // Kho đề gốc cannot be asked for (no "not equal" on the gateway); originalPage.ts pages it
  if (bankTab(filter) === 'ai-quiz-supporter') Object.assign(query, SOURCE_QUERY['ai-quiz-supporter']);
  if (filter.lessonRefUnitId) query['config.msb.lessonRef.unitId'] = filter.lessonRefUnitId;
  if (filter.type && filter.type !== 'all') Object.assign(query, TYPE_QUERY[filter.type]);
  else query.isFilterType = true;
  return query;
}

/** Paging keys for /question/admin, 1-based page, newest edit first like the admin. */
export function pageParams(page: number, size: number): ServerQuery {
  const current = Math.max(1, Math.floor(page));
  return { _limit: size, _skip: (current - 1) * size, _sort: 'updatedAt:desc' };
}

/**
 * Rows left for this page once the count is known. The server walks the newest-first index until it
 * has _limit matches, so asking past the last match walks the whole bank (prod: 3 matches, _limit 20
 * 2.6-23.5 s; _limit 3 0.2-0.5 s).
 */
export function pageLimit(total: number, page: number, size: number): number {
  const skip = (Math.max(1, Math.floor(page)) - 1) * size;
  return Math.max(0, Math.min(size, total - skip));
}

export function pageCount(total: number, size: number): number {
  return Math.max(1, Math.ceil(Math.max(0, total) / size));
}

export function clampPage(page: number, total: number, size: number): number {
  return Math.min(Math.max(1, Math.floor(page)), pageCount(total, size));
}

/** Page buttons around the current one, with 'gap' where numbers are skipped: 1 … 4 5 6 … 20. */
export function pageWindow(current: number, pages: number): (number | 'gap')[] {
  const wanted: number[] = [];
  for (let page = 1; page <= pages; page += 1) {
    if (page === 1 || page === pages || Math.abs(page - current) <= 1) wanted.push(page);
  }
  const out: (number | 'gap')[] = [];
  wanted.forEach((page, index) => {
    const previous = wanted[index - 1];
    if (previous !== undefined && page - previous === 2) out.push(previous + 1);
    else if (previous !== undefined && page - previous > 2) out.push('gap');
    out.push(page);
  });
  return out;
}
